import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Home from './pages/Home';
import Providers from './pages/Providers';
import ProviderDetail from './pages/ProviderDetail';
import ProviderForm from './pages/ProviderForm';
import Locations from './pages/Locations';
import AddLocation from './pages/AddLocation';
import EditLocation from './pages/EditLocation';
import Roles from './pages/Roles';
import AddRole from './pages/AddRole';
import EditRole from './pages/EditRole';
import Appointments from './pages/Appointments';
import {
	ProviderChangePassword,
	ProviderDashboard,
	ProviderIntegrations,
	ProviderPaymentSetup,
	ProviderSettings,
	ProviderStaffManagement,
	ProviderAppointments,
	ProviderTCRApprovals,
	ProviderTCRPayments,
	ProviderInvoices,
	ProviderCustomers,
	ProviderRevenue,
} from './pages/providers/index';
import { CustomerInvoicePay } from './pages/customers/index';
import {
	StaffProfile,
	StaffDashboard,
	StaffBookings,
	StaffTCR,
	StaffTCRForm,
	StaffAgreements,
	StaffPaymentSetup,
	StaffEarnings,
} from './pages/staff/index';
import ChangePassword from './pages/shared/ChangePassword';
import AdminSettings from './pages/Settings';
import useAuthStore from './stores/authStore';
import { USER_ROLE } from './types/auth';

const Router = () => {
	const { isAuthenticated, user } = useAuthStore();

	// Determine where authenticated users should go based on their role
	const getAuthenticatedRedirect = () => {
		if (!user) return '/login';

		// Role-specific password change redirects
		if (user.passwordChangeRequired) {
			if (user.role === USER_ROLE.STAFF) return '/staff/change-password';
			return '/providers/change-password';
		}

		// Redirect based on role
		switch (user.role) {
			case USER_ROLE.SUPER_ADMIN:
				return '/dashboard';
			case USER_ROLE.SALON_OWNER:
				return '/providers/dashboard';
			case USER_ROLE.STAFF:
				return '/staff/dashboard';
			default:
				return '/dashboard';
		}
	};

	return (
		<Routes>
			{/* Public Routes */}
			<Route
				path="/login"
				element={
					isAuthenticated ? (
						<Navigate to={getAuthenticatedRedirect()} replace />
					) : (
						<Login />
					)
				}
			/>

			{/* Customer Routes (public, no auth required) */}
			<Route path="/pay/:token" element={<CustomerInvoicePay />} />

			{/* Protected Routes */}
			<Route
				path="/"
				element={
					<ProtectedRoute>
						<Navigate to={getAuthenticatedRedirect()} replace />
					</ProtectedRoute>
				}
			/>

			{/* Super Admin Dashboard */}
			<Route
				path="/dashboard"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<Home />
					</ProtectedRoute>
				}
			/>

			{/* Super Admin Routes */}
			<Route
				path="/providers"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<Providers />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/new"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<ProviderForm />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/:id/edit"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<ProviderForm />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/:id"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<ProviderDetail />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/settings"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN]}>
						<AdminSettings />
					</ProtectedRoute>
				}
			/>

			{/* Provider (Salon Owner) Routes */}
			<Route
				path="/providers/change-password"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderChangePassword />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/dashboard"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderDashboard />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/payment-setup"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderPaymentSetup />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/integrations"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderIntegrations />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/staff-management"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderStaffManagement />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/appointments"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderAppointments />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/tcr-approvals"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderTCRApprovals />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/providers/tcr-payments"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderTCRPayments />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/providers/invoices"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderInvoices />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/providers/revenue"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderRevenue />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/providers/customers"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderCustomers />
					</ProtectedRoute>
				}
			/>

			{/* Salon Owner Routes */}
			<Route
				path="/locations"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<Locations />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/locations/new"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<AddLocation />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/locations/:id/edit"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<EditLocation />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/roles"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<Roles />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/roles/new"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<AddRole />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/roles/:id/edit"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<EditRole />
					</ProtectedRoute>
				}
			/>

			{/* Staff Routes */}
			<Route
				path="/staff/change-password"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<ChangePassword redirectTo="/staff/dashboard" />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/profile"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffProfile />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/agreements"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffAgreements />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/dashboard"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffDashboard />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/bookings"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffBookings />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/payment-setup"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffPaymentSetup />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/earnings"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffEarnings />
					</ProtectedRoute>
				}
			/>

			{/* Common Routes (placeholders) */}
			<Route
				path="/bookings"
				element={
					<ProtectedRoute>
						<Appointments />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/tcr/new"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffTCRForm />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/tcr/:id/edit"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffTCRForm />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/staff/tcr"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.STAFF]}>
						<StaffTCR />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/approvals"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN, USER_ROLE.SALON_OWNER]}>
						<div>Approvals (Coming Soon)</div>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/inventory"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SUPER_ADMIN, USER_ROLE.SALON_OWNER]}>
						<div>Inventory (Coming Soon)</div>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/commissions"
				element={
					<ProtectedRoute>
						<div>Commissions (Coming Soon)</div>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/providers/settings"
				element={
					<ProtectedRoute allowedRoles={[USER_ROLE.SALON_OWNER]}>
						<ProviderSettings />
					</ProtectedRoute>
				}
			/>

			{/* Fallback */}
			<Route path="*" element={<Navigate to={getAuthenticatedRedirect()} replace />} />
		</Routes>
	);
};

export default Router;
